const {Component} = require("react");
const PropTypes = require("prop-types");
const {classList} = require("helpers/html");
const {observer} = require("mobx-react");
const {computed} = require("mobx");
const {decodeObservable} = require("helpers/object");
const Stars = require("components/mixins/Stars");
const {moneyFormat} = require("helpers/format");
const router = require("services/router");
const moment = require("moment");
const {onSameDay, sameMoments} = require("helpers/moment");
const Load = require("components/mixins/Load");

@observer
class Navbar extends Component {

	static get propTypes() {
		return {
			defaultAvatarUrl: PropTypes.string,
			submitLabel: PropTypes.string,
			handleSubmit: PropTypes.func,
			renderExtras: PropTypes.func,
			renderPreOptions: PropTypes.func,
			renderOptions: PropTypes.func,
			renderFooter: PropTypes.func,
			renderVoucher: PropTypes.func,
			processTotal: PropTypes.func,
			store: PropTypes.object.isRequired,
			showClips: PropTypes.bool,
			availableClips: PropTypes.number,
			clipsLeft: PropTypes.number,
		};
	}

	static get defaultProps() {
		return {
			defaultAvatarUrl: "img/avatar-placeholder.png",
			submitLabel: "Næste",
			showClips: false,
			availableClips: 0,
			clipsLeft: 0,
		};
	}

	constructor(props) {
		super(props);
		this.state = {loading: false};
	}

	/////////////////////////////////
	// Configuration
	/////////////////////////////////

	get store() {
		return this.props.store;
	}

	@computed
	get storeData() {
		return decodeObservable(this.store.attributes);
	}

	@computed
	get bookable() {
		return this.storeData.bookable;
	}

	@computed
	get bookings() {
		return this.storeData.bookings;
	}

	@computed
	get groupedBookings() {
		return this.bookings
			.sort((bookingA, bookingB) => {
				return moment(bookingA.from).isBefore(moment(bookingB.from)) ? -1 : 1;
			})
			.reduce((acc, booking) => {
				let currentGroup = acc.length > 0 ? acc[acc.length - 1] : null;
				const from = moment(booking.from);
				const to = moment(booking.to);

				if ((currentGroup == null) || !onSameDay(currentGroup.from, from) || !sameMoments(currentGroup.to, from)) {
					currentGroup = {from, to, bookings: [booking]};
					acc.push(currentGroup);
					return acc;
				}

				currentGroup.to = to;
				currentGroup.bookings.push(booking);

				return acc;

			}, []);
	}

	get subtotal() {
		return parseFloat(this.storeData.unit_price) * this.sumBookingHours(this.bookings);
	}

	get total() {
		const total = this.subtotal;

		return typeof this.props.processTotal === "function" ?
			this.props.processTotal(total) :
			total;
	}

	get className() {
		return classList(
			"payment",
			"animate",
			"slide-in",
			this.state.loading ? "loading" : null,
			this.props.className
		);
	}

    sumBookingHours(bookings){
        let hours = 0;
        if(bookings.length > 0){
            for(let booking_index in bookings){
                let booking = bookings[booking_index];
                hours += booking.hours;
            }
        }
        return hours;
    }

	get hours(){
        let bookings = decodeObservable(this.bookings);
        return this.sumBookingHours(bookings);
    }

	/////////////////////////////////
	// Listeners & Actions
	/////////////////////////////////

	setLoading(loading = true) {
		this.setState({loading});
	}

	onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();

		this.setLoading(true);

		if (typeof this.props.handleSubmit === "function") {
			const result = this.props.handleSubmit();

			result && typeof result.then === "function" ?
				result.then(() => this.setLoading(false)) :
				this.setLoading(false);
		}
	};

	/////////////////////////////////
	// Rendering
	/////////////////////////////////

	renderBookable() {
		const bookable = this.bookable;
		const ratingMarkup = bookable.average_rating ? (<Stars filled={parseInt(bookable.average_rating)}/>) : null;
		const avatarUrl = bookable.avatar ? bookable.avatar.url : router.url(this.props.defaultAvatarUrl);

		return (
			<div className="top-bar">
				<div className="des-box">
					<h2>{bookable.nickname} {bookable.age}</h2>
					{ratingMarkup}
					<p className="price">{moneyFormat(bookable.hourly_rate)} <span>pr. time</span></p>
				</div>
				<div className="img-box">
					<div className="img-wrap object-fit">
						<img src={avatarUrl} alt="teacher"/>
					</div>
				</div>
			</div>
		);
	}

	renderBookings() {
		const groups = this.groupedBookings;
		const unitPrice = this.storeData.unit_price;

		return groups.map(group => {

			let hours = this.sumBookingHours(group.bookings);
			const key = group.from.format("YYYY-MM-DD HH:mm:ss");

			let price = this.props.showClips ?
                hours + ' klip' :
                moneyFormat(hours * parseFloat(unitPrice));

			return (
				<div key={key} className="class-row">
					<p>{group.from.format("DD. MMMM")} {group.from.format("HH:mm")} - {group.to.format("HH:mm")}</p>
					<p className="price">{price}</p>
				</div>
			);
		});
	}

	renderTotalClips() {
		if (!this.props.showClips) return;

		const total		= this.hours;
		const available	= this.props.availableClips;
		const left		= this.props.clipsLeft;

		let afterTotal = available ?
			(<p>{available - total} klip tilbage efter køb</p>) :
			null;

		afterTotal = !afterTotal && left ?
			(<p>{left} klip tilbage</p>) :
			afterTotal;

		return (
			<div className="price-box">
				<div className="total-price">
					<p>Antal klip:</p>
					<p>{total} klip</p>
				</div>

				{afterTotal}
			</div>
		);
	}

	renderVoucherInfo() {
		const info = (typeof this.props.renderVoucher === "function") ?
			this.props.renderVoucher() :
			null;

		return (
			<React.Fragment>
				{this.renderTotalClips()}
				{info}
			</React.Fragment>
		);
	}

	renderTotal() {
		const total = this.total;
		if (total === 0) return;

		return (
			<div className="price-box">
				<div className="total-price">
					<p>Totalbeløb:</p>
					<p>{moneyFormat(total)}</p>
				</div>

				<p>Inkl. moms</p>
			</div>
		);
	}

	renderExtras() {
		if (this.props.showClips) return;

		if (typeof this.props.renderExtras === "function") {
			return (
				<div className="payment-extras">{this.props.renderExtras()}</div>
			);
		}
	}

	renderPreOptions() {
		if (typeof this.props.renderPreOptions === "function") {
			return this.props.renderPreOptions();
		}
	}

	renderOptions() {
		if (typeof this.props.renderOptions === "function") {
			return (
				<div className="payment-options">{this.props.renderOptions()}</div>
			);
		}
	}

	renderFooter() {
		if (typeof this.props.renderFooter === "function") {
			return (
				<div className="payment-footer">{this.props.renderFooter()}</div>
			);
		}
	}

	render() {
		return (
			<div className={this.className}>
				{this.renderBookable()}

				{this.renderPreOptions()}

				<div className="classes">
					{this.renderBookings()}
					{this.renderExtras()}
				</div>

				{this.renderVoucherInfo()}
				{this.renderTotal()}
				{this.renderOptions()}

				<div className="buy">
					<button type="button" className="btn uppercase" onClick={this.onSubmit}>{this.props.submitLabel}
						<Load></Load>
					</button>
					{this.renderFooter()}
				</div>
			</div>
		);
	}

}

module.exports = Navbar;



// WEBPACK FOOTER //
// ./resources/js/components/checkout/Navbar.js